<script>
import { GlEmptyState, GlSkeletonLoader } from '@gitlab/ui';
import { MountingPortal } from 'portal-vue';
import emptyStateSvg from '@gitlab/svgs/dist/illustrations/empty-state/empty-activity-md.svg';
import DynamicPanel from '~/vue_shared/components/dynamic_panel.vue';
import { n__ } from '~/locale';
import { updateHistory, setUrlParams, removeParams } from '~/lib/utils/url_utility';
import { DECISION_LOG_PANEL, DETAIL_VIEW_QUERY_PARAM_NAME } from '~/work_items/constants';
import { getRequestedPanel } from '~/work_items/utils';
import DecisionLogItem from './decision_log_item.vue';

export default {
  name: 'DecisionLogPanel',
  components: {
    DecisionLogItem,
    DynamicPanel,
    GlEmptyState,
    GlSkeletonLoader,
    MountingPortal,
  },
  inheritAttrs: false,
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    decisions: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['close'],
  computed: {
    capturedHeading() {
      return n__('%d decision captured', '%d decisions captured', this.decisions.length);
    },
  },
  watch: {
    open(value) {
      if (value) {
        this.addPanelParam();
      } else {
        this.removePanelParam();
      }
    },
  },
  created() {
    if (this.open) {
      this.addPanelParam();
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    addPanelParam() {
      if (getRequestedPanel() === DECISION_LOG_PANEL) return;

      updateHistory({
        url: setUrlParams({ [DETAIL_VIEW_QUERY_PARAM_NAME]: DECISION_LOG_PANEL }),
      });
    },
    // Only clear the param while it still points here, so closing this panel cannot wipe the deep
    // link another panel just wrote.
    removePanelParam() {
      if (getRequestedPanel() !== DECISION_LOG_PANEL) return;

      updateHistory({ url: removeParams([DETAIL_VIEW_QUERY_PARAM_NAME]) });
    },
    // Escape belongs to whatever the user is typing in, or to the modal on top, before it belongs
    // to this panel.
    hasPriorEscapeHandler() {
      const active = document.activeElement;

      return (
        document.body.classList.contains('modal-open') ||
        active?.closest('.js-editor') != null ||
        // Rich text fields are contenteditable, and focus can sit on a node inside one.
        active?.closest('[contenteditable]:not([contenteditable="false"])') != null ||
        active?.tagName === 'INPUT' ||
        active?.tagName === 'TEXTAREA'
      );
    },
    handleKeydown({ key }) {
      if (key === 'Escape' && this.open && !this.hasPriorEscapeHandler()) {
        this.$emit('close');
      }
    },
  },
  emptyStateSvg,
};
</script>

<template>
  <mounting-portal v-if="open" mount-to="#contextual-panel-portal" append>
    <!-- DynamicPanel rather than a hand-rolled panel: its .panel-content-inner is the scroll
      container, so a long log scrolls instead of overflowing the panel. -->
    <dynamic-panel
      :header="s__('WorkItemDecisionLog|Decision log')"
      data-testid="decision-log-panel"
      @close="$emit('close')"
    >
      <gl-skeleton-loader v-if="isLoading" data-testid="decision-log-loading" />
      <template v-else-if="decisions.length">
        <h2 class="gl-heading-3 gl-mb-5 gl-mt-4" data-testid="decision-log-count">
          {{ capturedHeading }}
        </h2>
        <ul class="gl-m-0 gl-p-0">
          <decision-log-item
            v-for="decision in decisions"
            :key="decision.id"
            :decision="decision"
            @view-comment="$emit('close')"
          />
        </ul>
      </template>
      <gl-empty-state
        v-else
        data-testid="decision-log-empty-state"
        :svg-path="$options.emptyStateSvg"
        :svg-height="144"
        content-class="!gl-px-0"
        :title="s__('WorkItemDecisionLog|No decisions yet')"
        :description="
          s__(
            'WorkItemDecisionLog|Decisions show up here once an open question gets answered or a comment is marked as a decision. Each one keeps the background behind it and a link back to where it was made.',
          )
        "
      />
    </dynamic-panel>
  </mounting-portal>
</template>
